import { FCC } from "@/types/common";
import { Stack, styled } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "@/components/elements/Form";
import { Button } from "@/components/elements/Button";
import { IconSave } from "@/assets/icons";
import { createPartnerApi, updatePartnerApi } from "@/api/services/main/partner";
import { convertPartnerData } from "@/features/talent/utils/convertPartnerData";
import { PartnerTitle } from "@/features/partner/components/PartnerTitle";
import { PartnerFooterContainer } from "@/features/partner/components/PartnerFooterContainer";
import { PartnerBasicInfoForm } from "@/features/partner/components/partner-info/PartnerBasicInfoForm";
import { PartnerContactInfoForm } from "@/features/partner/components/partner-info/PartnerContactInfoForm";
import { PartnerContractInfoForm } from "@/features/partner/components/partner-info/PartnerContractInfoForm";
import { PartnerAdditionalInfoForm } from "@/features/partner/components/partner-info/PartnerAdditionalInfoForm";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { useGetPartnerSchema } from "@/features/partner/hooks/useGetPartnerSchema";
import { TPartnerForm } from "@/features/talent/types/partnerForm";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";

type TPartnerFormProps = {
  defaultValues?: Partial<TPartnerForm>;
  readonly?: boolean;
  managementNumber?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export const PartnerForm: FCC<TPartnerFormProps> = ({ defaultValues, readonly, managementNumber, createdAt, updatedAt }) => {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const { partnerSchema } = useGetPartnerSchema();

  const methods = useForm<TPartnerForm>({
    defaultValues: { ...defaultValues },
    resolver: yupResolver(partnerSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [isChangeValue, setIsChangeValue] = useState(false);
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<{ route: string } | null>(null);

  const watchValue = methods.watch();

  useEffect(() => {
    const hasValue = Object.values(watchValue).some((value) => {
      if (value === null || value === "" || value === undefined || value === false) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
    setIsChangeValue(hasValue);
  }, [watchValue]);

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect.route);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const onSubmit = async (data: TPartnerForm) => {
    setIsDisableButton(true);
    try {
      const payload = convertPartnerData({ ...data, nationalityId: Number(data.nationalityId) });
      let route: string;
      let message: string | undefined;

      if (partnerId) {
        const rs = await updatePartnerApi(partnerId, { ...payload, id: Number(partnerId) });
        message = rs.message;
        route = getDynamicRoute(AppRoutes.partnerDetail, { partnerId });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PARTNER.FETCH_DETAIL, partnerId] });
      } else {
        const rs = await createPartnerApi(payload);
        message = rs.message;
        route = AppRoutes.partnerList;
      }
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST] });

      toast.success(message ?? API_SUCCESS_MESSAGE);
      setIsDisableButton(false);
      setBlockerEnabled(false);
      setRedirect({ route });
    } catch (e) {
      setIsDisableButton(false);
      showError(e);
    }
  };

  return (
    <>
      <PartnerTitle title={defaultValues?.partnerName} managementNumber={managementNumber} createdAt={createdAt} updatedAt={updatedAt} />

      <Stack mt={3}>
        <Form methods={methods} onSubmit={(data) => onSubmit(data)} schema={partnerSchema} enableBlocker={blockerEnabled && isChangeValue}>
          <MenuNavigate
            contents={[
              { title: "基本情報", content: <PartnerBasicInfoForm readonly={readonly} /> },
              { title: "担当者情報", content: <PartnerContactInfoForm readonly={readonly} /> },
              { title: "契約情報", content: <PartnerContractInfoForm readonly={readonly} /> },
              { title: "その他の情報", content: <PartnerAdditionalInfoForm readonly={readonly} /> },
            ]}
            contentWidth="900px"
            contentAffix={
              <PartnerFooterContainer>
                <ButtonSave disabled={isDisableButton} type="submit" variant="primary" size="sm" startIcon={<IconSave />}>
                  保存
                </ButtonSave>
              </PartnerFooterContainer>
            }
          />
        </Form>
      </Stack>
    </>
  );
};

const ButtonSave = styled(Button)`
  width: 88px;
`;
